<template>
  <div class="corp-container">

    <!-- 装饰光晕背景 -->
    <div class="bg-glow bg-glow--blue" aria-hidden="true"></div>
    <div class="bg-glow bg-glow--orange" aria-hidden="true"></div>

    <!-- ===== 顶部横幅区域（保持不变） ===== -->
    <section class="main-visual-section">
      <div class="visual-image">
        <img src="/eprinttools-files/front-end/corp.png" alt="包装印刷解决方案" />
        <div class="visual-overlay" aria-hidden="true"></div>
        <div class="visual-overlay-bottom" aria-hidden="true"></div>
      </div>
    </section>

    <!-- ===== 关于我们描述区域（关键词变色） ===== -->
    <section class="about-description-section">
      <div class="section-header">
        <h2 class="section-title">关于我们</h2>
        <p class="section-subtitle">用代码为包装印刷业注入效率与品质的生命力</p>
      </div>
      <div class="description-content">
        <p class="lead-paragraph">
          我们不是普通的软件开发者，而是紧盯包装印刷领域的
          <span class="highlight-brand">"效率优化师"</span>
          与
          <span class="highlight-warm">"品质守护者"</span>。
        </p>
        <p>我们见过太多行业伙伴在重复操作中消耗精力，在细节误差里损耗成本。于是，我们带着对印刷工艺的敬畏和对技术的热爱，一头扎进这个领域 —— 不做泛泛的通用工具，只聚焦包装印刷的每一个核心环节，把 "提高效率、优化生产、提升质量" 这三个目标，拆成一行行精准的代码，变成一个个能落地的软件与插件。</p>
        <p>团队里既有懂印刷工艺的 "老行家"，能精准捕捉生产中的痛点；也有懂技术研发的 "极客"，能把解决方案转化为易用的工具。我们不追逐花哨的技术概念，只在乎每一款产品是否能让设计师少加班、让生产线少返工、让最终的包装作品更完美。</p>
        <p>成立至今，我们始终和行业同频：客户的每一次反馈，都是我们迭代的方向；行业的每一个新需求，都是我们创新的起点。我们坚信，好的技术不该是冰冷的代码，而应是能真正融入生产、解决问题的 "得力帮手"，这也是我们始终不变的坚持。</p>
      </div>
    </section>

    <!-- ===== 核心价值模块（新增） ===== -->
    <section class="core-values-section">
      <div class="section-header">
        <h2 class="section-title">核心价值</h2>
      </div>
      <div class="values-grid">
        <div
            class="value-card"
            v-for="(value, index) in coreValues"
            :key="value.id"
            :style="{ '--stagger': index + 's' }"
        >
          <div class="value-icon" :style="{ background: value.gradient }" v-html="value.iconSvg"></div>
          <h3 class="value-title">{{ value.title }}</h3>
          <p class="value-desc">{{ value.desc }}</p>
        </div>
      </div>
    </section>

    <!-- ===== 精选工具区域（样式与动画重构） ===== -->
    <section class="featured-tools-section">
      <div class="section-header">
        <h2 class="section-title">精选工具</h2>
      </div>
      <div class="tools-grid">
        <div
            class="tool-card"
            v-for="(tool, index) in toolsList"
            :key="tool.id"
            :style="{ '--stagger': index + 's' }"
            @click="goToPluginDetails(tool.id)"
        >
          <div class="tool-image-wrapper">
            <img
                class="tool-image"
                :src="tool.image"
                :alt="tool.name"
                loading="lazy"
            />
          </div>
          <div class="tool-body">
            <h3 class="tool-title">{{ tool.name }}</h3>
            <div class="tool-action">
              <span>查看详情</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tool-arrow" aria-hidden="true">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </div>
          </div>
          <div class="tool-bar" aria-hidden="true"></div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { reactive } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const goToPluginDetails = (pluginId) => {
  router.push({
    path: '/products',
    query: { id: pluginId }
  });
};

// ===== 工具列表（数据保持不变） =====
const toolsList = reactive([
  {
    id: 2,
    name: "企业级二维码插件",
    image: '/eprinttools-files/front-end/code.gif'
  },
  {
    id: 3,
    name: "智能刀模生成插件",
    image: '/eprinttools-files/front-end/mo.gif'
  },
  {
    id: 4,
    name: "美国版能效标签插件",
    image: '/eprinttools-files/front-end/neng.gif'
  },
  {
    id: 5,
    name: "美国版能效标签插件",
    image: '/eprinttools-files/front-end/huang.gif'
  }
]);

// ===== 核心价值数据 =====
const coreValues = reactive([
  {
    id: 1,
    title: '专注深耕',
    desc: '只聚焦包装印刷领域，做最懂行业的工具',
    gradient: 'linear-gradient(135deg, #2f5bff, #34d399)',
    iconSvg: `<svg width="23" height="23" viewBox="0 0 1024 1024" fill="white" stroke="white" stroke-width="3" xmlns="http://www.w3.org/2000/svg"><path d="M512 0c282.7648 0 512 229.2352 512 512S794.7648 1024 512 1024 0 794.7648 0 512 229.2352 0 512 0z m0 93.0944C280.64 93.0944 93.0944 280.64 93.0944 512c0 231.36 187.5456 418.9056 418.9056 418.9056 231.36 0 418.9056-187.5456 418.9056-418.9056C930.9056 280.64 743.36 93.0944 512 93.0944z m0 93.0816c179.9424 0 325.824 145.8816 325.824 325.824 0 179.9424-145.8816 325.824-325.824 325.824-179.9424 0-325.824-145.8816-325.824-325.824 0-179.9424 145.8816-325.824 325.824-325.824z m0 93.0944c-128.5376 0-232.7296 104.192-232.7296 232.7296 0 128.5376 104.192 232.7296 232.7296 232.7296 128.5376 0 232.7296-104.192 232.7296-232.7296 0-128.5376-104.192-232.7296-232.7296-232.7296z m0 93.0944c77.12 0 139.648 62.5152 139.648 139.6352S589.1072 651.648 512 651.648 372.352 589.1072 372.352 512 434.8928 372.352 512 372.352z m0 93.0944a46.5408 46.5408 0 1 0 0 93.0816 46.5408 46.5408 0 0 0 0-93.0816z" /></svg>`
  },
  {
    id: 2,
    title: '用户至上',
    desc: '每一次反馈都是迭代的方向，真正解决痛点',
    gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
    iconSvg: `<svg width="23" height="23" viewBox="0 0 1024 1024" fill="white" stroke="white" stroke-width="3" xmlns="http://www.w3.org/2000/svg"><path d="M512.042667 193.237333a255.914667 255.914667 0 0 1 351.658666 9.728 256 256 0 0 1 10.069334 351.402667l-361.813334 362.325333-361.728-362.325333a256 256 0 0 1 361.813334-361.130667z m291.242666 70.016a170.581333 170.581333 0 0 0-234.24-6.528l-56.96 51.114667-57.002666-51.072a170.666667 170.666667 0 0 0-242.602667 239.146667L512 795.904l299.52-299.946667a170.666667 170.666667 0 0 0-8.234667-232.704z" /></svg>`
  },
  {
    id: 3,
    title: '持续创新',
    desc: '紧跟行业新需求，不断推出前沿解决方案',
    gradient: 'linear-gradient(135deg, #f43f5e, #f472b6)',
    iconSvg: `<svg width="23" height="23" viewBox="0 0 1025 1024" fill="white" stroke="white" stroke-width="3" xmlns="http://www.w3.org/2000/svg"><path d="M187.732004 156.526779c-13.330296-13.331627-34.948251-13.331627-48.263899 0-13.335622 13.326301-13.335622 34.937598 0 48.274552l48.263899 48.263899c13.331627 13.331627 34.933604 13.331627 48.269226 0 13.326301-13.326301 13.326301-34.932272 0-48.263899L187.732004 156.526779 187.732004 156.526779 187.732004 156.526779zM836.261339 156.526779l-48.269226 48.274552c-13.326301 13.331627-13.326301 34.937598 0 48.263899 13.315648 13.331627 34.932272 13.331627 48.269226 0l48.258573-48.263899c13.336953-13.336953 13.336953-34.942925 0-48.274552C871.194942 143.200478 849.592966 143.200478 836.261339 156.526779L836.261339 156.526779 836.261339 156.526779zM102.402665 426.66267 34.139994 426.66267C15.28903 426.66267 0.002666 441.945039 0.002666 460.789345c0 18.854958 15.28237 34.137328 34.137328 34.137328l68.262672 0c18.850963 0 34.132002-15.28237 34.132002-34.137328C136.534667 441.945039 121.253629 426.66267 102.402665 426.66267L102.402665 426.66267 102.402665 426.66267zM989.85468 426.66267l-68.264003 0c-18.849632 0-34.137328 15.28237-34.137328 34.126675 0 18.854958 15.286364 34.137328 34.137328 34.137328l68.264003 0c18.849632 0 34.142654-15.28237 34.142654-34.137328C1023.997334 441.945039 1008.704312 426.66267 989.85468 426.66267L989.85468 426.66267 989.85468 426.66267zM477.862672 34.132002l0 68.264003c0 18.854958 15.28237 34.13067 34.132002 34.13067 18.844306 0 34.137328-15.275712 34.137328-34.13067L546.132002 34.132002C546.132002 15.28237 530.838979 0 511.994674 0 493.145042 0 477.862672 15.28237 477.862672 34.132002L477.862672 34.132002 477.862672 34.132002zM273.066668 511.992008c0-131.955413 106.967267-238.928006 238.928006-238.928006 131.960739 0 238.933332 106.972593 238.933332 238.928006 0 131.960739-106.972593 238.937327-238.933332 238.937327C380.033935 750.929335 273.066668 643.951415 273.066668 511.992008L273.066668 511.992008 273.066668 511.992008zM204.803996 511.992008c0 169.675982 137.525348 307.211982 307.190677 307.211982S819.190677 681.667989 819.190677 511.992008c0-169.665329-137.530675-307.190677-307.196004-307.190677S204.803996 342.326679 204.803996 511.992008L204.803996 511.992008 204.803996 511.992008zM443.730671 989.862667c0 18.838979 15.28237 34.137328 34.132002 34.137328l68.269329 0c18.849632 0 34.132002-15.297017 34.132002-34.137328 0-18.860284-15.28237-34.132002-34.132002-34.132002l-68.269329 0C459.01304 955.730665 443.730671 971.002382 443.730671 989.862667L443.730671 989.862667 443.730671 989.862667zM375.467999 887.466662c0 18.834985 15.281038 34.132002 34.126675 34.132002l204.799999 0c18.849632 0 34.132002-15.297017 34.132002-34.132002 0-18.865611-15.28237-34.142654-34.132002-34.142654L409.594674 853.324008C390.743711 853.325339 375.467999 868.601051 375.467999 887.466662L375.467999 887.466662 375.467999 887.466662zM375.467999 887.466662" /></svg>`
  },
  {
    id: 4,
    title: '团队协作',
    desc: '懂工艺的老行家与懂技术的极客完美配合',
    gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
    iconSvg: `<svg width="23" height="23" viewBox="0 0 1024 1024" fill="white" stroke="white" stroke-width="3" xmlns="http://www.w3.org/2000/svg"><path d="M544 534.4c57.6-41.6 96-108.8 96-182.4 0-124.8-99.2-224-224-224s-224 99.2-224 224c0 76.8 38.4 144 96 182.4C156.8 585.6 64 713.6 64 864v32h64v-32c0-160 128-288 288-288s288 128 288 288c0 9.6 0 22.4-3.2 32h64v-32C768 713.6 675.2 585.6 544 534.4zM416 512c-89.6 0-160-70.4-160-160s70.4-160 160-160 160 70.4 160 160-70.4 160-160 160z" /><path d="M780.8 502.4C812.8 473.6 832 432 832 384c0-80-57.6-147.2-134.4-156.8l-9.6 64c44.8 6.4 80 48 80 96 0 54.4-41.6 96-96 96v64c124.8 0 224 99.2 224 224 0 9.6 0 22.4-3.2 32h64c0-9.6 3.2-22.4 3.2-32 0-124.8-73.6-227.2-179.2-268.8z" /></svg>`
  }
]);

</script>

<style scoped>
/* ============================================================
   1. 容器与背景（保留原有光晕）
   ============================================================ */
.corp-container {
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #0f172a;
  line-height: 1.6;
  background: radial-gradient(ellipse 800px 600px at 10% 0%, rgba(47, 91, 255, 0.08), transparent 60%),
  radial-gradient(ellipse 700px 500px at 90% 10%, rgba(245, 158, 11, 0.06), transparent 55%),
  #f7f9fc;
  overflow: hidden;
}

.bg-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
  animation: glow 8s ease-in-out infinite;
}

.bg-glow--blue {
  width: 500px;
  height: 500px;
  top: 30%;
  left: -150px;
  background: radial-gradient(circle, rgba(47, 91, 255, 0.18), transparent 70%);
}

.bg-glow--orange {
  width: 460px;
  height: 460px;
  top: 70%;
  right: -120px;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.14), transparent 70%);
  animation-delay: 2s;
}

@keyframes glow {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

/* ============================================================
   2. 顶部横幅（完全保持不变）
   ============================================================ */
.main-visual-section {
  position: relative;
  z-index: 1;
  padding: 0;
  width: 100%;
}

.visual-image {
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
  border-bottom-left-radius: 28px;
  border-bottom-right-radius: 28px;
}

.visual-image img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

.visual-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(47, 91, 255, 0.45) 0%, rgba(47, 91, 255, 0.1) 40%, rgba(245, 158, 11, 0.35) 100%);
  pointer-events: none;
  mix-blend-mode: multiply;
}

.visual-overlay-bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 120px;
  background: linear-gradient(to top, #f7f9fc 0%, rgba(248, 250, 252, 0) 100%);
  pointer-events: none;
}

/* ============================================================
   3. 区块标题通用
   ============================================================ */
.section-header {
  position: relative;
  z-index: 1;
  text-align: center;
  margin-bottom: 56px;
}

.section-title {
  font-size: 2.4rem;
  --spacing: 0.25rem;
  font-weight: 800;
  background: linear-gradient(135deg, #6296ea 0%, #72a2f1 60%, #f59e0b 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 14px 0;
  display: inline-block;
  position: relative;
  letter-spacing: 0.5px;
}

.section-title::after {
  content: '';
  display: block;
  width: calc(var(--spacing)* 32);
  height: 1px;
  background: linear-gradient(90deg, #0000, #0ea5e933, #f59e0b26, #0000);
  box-shadow: none;
  margin: 16px auto 0;
}

.section-divider {
  width: 128px;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(90deg, #2f5bff, #f59e0b);
  margin: 16px auto 0;
  box-shadow: 0 4px 12px rgba(47, 91, 255, 0.2);
}

.section-subtitle {
  font-size: 1.05rem;
  color: #475569;
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.7;
  font-weight: 500;
}

/* ============================================================
   4. 关于我们描述（关键词变色 + 重点样式）
   ============================================================ */
.about-description-section {
  position: relative;
  z-index: 1;
  padding: 90px 20px 40px;
  max-width: 1200px;
  margin: 0 auto;
}

.description-content {
  max-width: 860px;
  margin: 0 auto;
  position: relative;
}

.description-content p {
  margin: 0 0 22px;
  font-size: 1.05rem;
  color: #475569;
  line-height: 2;
  letter-spacing: 0.01em;
  text-align: justify;
  animation: fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.description-content p:nth-child(1) {
  animation-delay: 0.05s;
}
.description-content p:nth-child(2) {
  animation-delay: 0.15s;
}
.description-content p:nth-child(3) {
  animation-delay: 0.25s;
}
.description-content p:nth-child(4) {
  animation-delay: 0.35s;
}

.description-content .lead-paragraph {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.7;
  padding-left: 18px;
  border-left: 4px solid;
  border-image: linear-gradient(180deg, #2f5bff, #f59e0b) 1;
  margin-bottom: 28px;
}

/* —— 关键词高亮 —— */
.highlight-brand {
  font-weight: 700;
  color: #1f43cc;
  background: linear-gradient(135deg, #1f43cc 0%, #2f5bff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  display: inline-block;
}

.highlight-warm {
  font-weight: 700;
  color: #d97706;
  background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  display: inline-block;
}


/* ============================================================
   5. 核心价值模块（新增）
   ============================================================ */
.core-values-section {
  position: relative;
  z-index: 1;
  padding: 40px 20px 60px;
  max-width: 1200px;
  margin: 0 auto;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px;
}

.value-card {
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  padding: 32px 24px 28px;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  text-align: left;
  animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: calc(var(--stagger, 0) * 0.1s + 0.05s);
  cursor: default;
}

.value-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(47, 91, 255, 0.10);
  border-color: rgba(47, 91, 255, 0.2);
}

.value-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0 0 18px 0;
  border-radius: 14px;
  transition: transform 0.15s ease-out;
  will-change: transform;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
}

.value-card:hover .value-icon {
  transform: scale(1.10);
}

.value-icon svg {
  width: 100%;
  height: 100%;
  display: block;
}

.value-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px 0;
}

.value-desc {
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

/* ============================================================
   6. 精选工具（样式与动画重构）
   ============================================================ */
.featured-tools-section {
  position: relative;
  z-index: 1;
  padding: 40px 20px 90px;
  max-width: 1200px;
  margin: 0 auto;
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px;
}

/* —— 工具卡片 —— */
.tool-card {
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: calc(var(--stagger, 0) * 0.1s + 0.1s);
}

.tool-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 16px 48px rgba(47, 91, 255, 0.12);
  border-color: rgba(47, 91, 255, 0.25);
}

/* 图片区域 */
.tool-image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, #eef3ff 0%, #fff7ed 100%);
}

.tool-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.tool-card:hover .tool-image {
  transform: scale(1.10);
}

/* 卡片主体 */
.tool-body {
  padding: 20px 20px 18px;
  text-align: center;
}

.tool-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 10px 0;
  transition: color 0.3s ease;
}

.tool-card:hover .tool-title {
  color: #1f43cc;
}

/* 查看详情（hover 淡入） */
.tool-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #2f5bff;
  opacity: 0;
  transform: translateY(4px);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.tool-card:hover .tool-action {
  opacity: 1;
  transform: translateY(0);
}

.tool-arrow {
  width: 16px;
  height: 16px;
  stroke: #2f5bff;
  transition: transform 0.3s ease;
}

.tool-card:hover .tool-arrow {
  transform: translateX(4px);
}

/* 底部渐变条（hover 展开） */
.tool-bar {
  height: 2px;
  width: 100%;
  background: linear-gradient(90deg, #2f5bff, #f59e0b);
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.tool-card:hover .tool-bar {
  transform: scaleX(1);
}

/* ============================================================
   7. 入场动画
   ============================================================ */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============================================================
   8. 响应式
   ============================================================ */
@media (max-width: 1200px) {
  .tools-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .values-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .about-description-section {
    padding: 56px 16px 24px;
  }
  .core-values-section {
    padding: 24px 16px 40px;
  }
  .featured-tools-section {
    padding: 24px 16px 56px;
  }

  .section-header {
    margin-bottom: 36px;
  }
  .section-title {
    font-size: 1.8rem;
  }
  .section-subtitle {
    font-size: 0.98rem;
    padding: 0 8px;
  }

  .tools-grid {
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }
  .values-grid {
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }

  .visual-image {
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
  }
  .visual-image img {
    object-fit: contain;
  }

  .description-content p {
    font-size: 0.98rem;
    line-height: 1.85;
    margin-bottom: 18px;
  }
  .description-content .lead-paragraph {
    font-size: 1.1rem;
    padding-left: 14px;
  }

  .tool-image-wrapper {
    height: 160px;
  }
  .tool-body {
    padding: 16px 14px 14px;
  }
  .tool-title {
    font-size: 0.98rem;
  }

  .value-card {
    padding: 24px 16px 20px;
  }
  .value-icon {
    width: 48px;
    height: 48px;
  }
  .value-icon svg {
    width: 100%;
    height: 100%;
  }
  .value-title {
    font-size: 1rem;
  }
  .value-desc {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .about-description-section {
    padding: 44px 14px 16px;
  }
  .core-values-section {
    padding: 16px 14px 32px;
  }
  .featured-tools-section {
    padding: 16px 14px 44px;
  }

  .section-title {
    font-size: 1.6rem;
  }
  .section-subtitle {
    font-size: 0.92rem;
  }

  .tools-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .values-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .tool-image-wrapper {
    height: 180px;
  }
  .tool-body {
    padding: 18px 16px 14px;
  }

  .value-card {
    padding: 20px 16px 18px;
  }
}
</style>